import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/site/MarketingNav";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Calendar, Loader2, Copy, Sparkles } from "lucide-react";

export default function FreeWeek() {
  const [idea, setIdea] = useState("");
  const [output, setOutput] = useState(null);
  const [busy, setBusy] = useState(false);
  const [quota, setQuota] = useState({ used: 0, limit: 3, remaining: 3 });

  useEffect(() => {
    api.get("/public/quota").then((r) => setQuota(r.data)).catch(() => {});
  }, []);

  async function handleRun() {
    if (!idea.trim()) {
      toast.error("Give us one idea.");
      return;
    }
    setBusy(true);
    setOutput(null);
    try {
      const r = await api.post("/public/free-week", { idea });
      setOutput(r.data.output);
      const q = await api.get("/public/quota");
      setQuota(q.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong";
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  }

  function copyOut() {
    if (!output) return;
    navigator.clipboard.writeText(output);
    toast.success("Copied");
  }

  return (
    <div className="min-h-screen bg-background" data-testid="free-week-page">
      <MarketingNav />

      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto p-8 md:p-16 grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-7 lg:pr-16">
            <div className="overline text-muted-foreground flex items-center gap-2">
              <Calendar className="w-3 h-3 text-primary" /> The Free Week Generator
            </div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-none tracking-tight mt-4">
              Give us
              <br />
              <span className="italic text-primary">one idea.</span>
              <br />
              Get seven days.
            </h1>
            <p className="mt-6 max-w-xl text-muted-foreground">
              A live seven-day content calendar for your business — blog, social, email, SEO,
              LinkedIn, video and newsletter. Free. No signup. No credit card.
            </p>

            <div className="mt-10">
              <label className="overline text-muted-foreground">Your idea</label>
              <div className="mt-2 flex flex-col md:flex-row gap-2">
                <Input
                  value={idea}
                  onChange={(e) => setIdea(e.target.value)}
                  placeholder="I'm opening a queer-owned coffee shop in Los Angeles."
                  className="rounded-none h-12 font-mono text-sm flex-1"
                  data-testid="free-week-idea"
                  onKeyDown={(e) => e.key === "Enter" && handleRun()}
                />
                <Button
                  onClick={handleRun}
                  disabled={busy}
                  className="rounded-none h-12 px-6 font-mono uppercase tracking-widest text-xs"
                  data-testid="free-week-run"
                >
                  {busy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" /> Generate 7 days
                    </>
                  )}
                </Button>
              </div>
              <div className="mt-3 overline text-[10px] text-muted-foreground">
                {quota.remaining} of {quota.limit} free runs left today
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 mt-10 lg:mt-0 bg-foreground text-background p-8 md:p-10 border border-background/10">
            <div className="overline opacity-60">What you'll get</div>
            <ul className="mt-4 space-y-3 text-sm">
              {[
                "7 days · one channel per day",
                "Blog, Instagram, Email, SEO, LinkedIn, Video, Newsletter",
                "Ready to copy, ready to ship",
                "Zero fluff · zero jargon",
              ].map((x) => (
                <li key={x} className="flex items-start gap-3">
                  <span className="w-1 h-1 rounded-full bg-primary mt-2" />
                  <span className="opacity-90">{x}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 border-t border-background/10 pt-6 text-sm opacity-80">
              After your week is ready → open Draftwave to generate the{" "}
              <span className="text-primary">entire campaign</span>. 120 free credits included.
            </div>
          </div>
        </div>
      </section>

      {(output || busy) && (
        <section className="border-b border-border">
          <div className="max-w-[1400px] mx-auto p-6 md:p-12">
            <div className="flex items-center justify-between">
              <div className="overline text-muted-foreground">Your week</div>
              {output && (
                <Button
                  onClick={copyOut}
                  variant="outline"
                  size="sm"
                  className="rounded-none h-8 font-mono text-[10px] uppercase tracking-widest"
                  data-testid="free-week-copy"
                >
                  <Copy className="w-3 h-3" /> Copy
                </Button>
              )}
            </div>
            {busy && (
              <div className="mt-8 flex flex-col items-center justify-center gap-3 py-16">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <div className="overline text-muted-foreground text-[10px]">
                  Composing your week…
                </div>
              </div>
            )}
            {output && (
              <>
                <pre
                  className="mt-6 whitespace-pre-wrap font-mono text-sm leading-relaxed bg-background border border-border p-5 md:p-8 max-h-[70vh] overflow-auto"
                  data-testid="free-week-output"
                >
                  {output}
                </pre>
                <div className="mt-8 p-6 md:p-8 border border-primary bg-primary/5 flex flex-col md:flex-row items-start md:items-center gap-4">
                  <div className="flex-1">
                    <div className="font-serif-display text-2xl md:text-3xl leading-tight">
                      Want to generate the entire campaign?
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Open Draftwave — 120 free credits, no credit card.
                    </div>
                  </div>
                  <Link to="/login" data-testid="free-week-upsell">
                    <Button className="rounded-none h-12 px-6 font-mono uppercase tracking-widest text-xs">
                      Open Draftwave <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
